import React, { useMemo, useState } from "react";

function StatusPill({ type }) {
  const map = {
    active: "bg-[#EAF7EE] text-[#1F7A3A]",
    inactive: "bg-[#FFECEC] text-[#B91C1C]",
  };
  const label = {
    active: "Активен",
    inactive: "Не работает",
  };

  return (
    <span
      className={[
        "inline-flex items-center h-6 px-3 rounded-md text-[12px] font-medium",
        map[type] || map.active,
      ].join(" ")}
    >
      {label[type] || "Активен"}
    </span>
  );
}

function ActionBtn({ variant = "gray", children, onClick }) {
  const map = {
    gray: "bg-[#EEF2FF] text-[#374151]",
    red: "bg-[#FFECEC] text-[#B91C1C]",
    yellow: "bg-[#FFF3D6] text-[#8A6A00]",
    green: "bg-[#EAF7EE] text-[#1F7A3A]",
  };

  return (
    <button
      className={[
        "h-7 px-3 rounded-md text-[12px] font-medium hover:opacity-90 transition",
        map[variant] || map.gray,
      ].join(" ")}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * rows ожидает массив из API /api/proxies:
 * [{ uid, proxyUrl, status, accountsValue, createdAt, ... }]
 */
export default function ProxyTable({
  rows = [],
  onEditOne,
  onRecheckOne,
  onSelectionChange,
}) {
  const [selected, setSelected] = useState(() => new Set());

  const allIds = useMemo(() => rows.map((r) => r.uid), [rows]);
  const allChecked = rows.length > 0 && selected.size === rows.length;
  const indeterminate = selected.size > 0 && selected.size < rows.length;

  function emit(nextSet) {
    setSelected(nextSet);
    onSelectionChange?.(Array.from(nextSet));
  }

  function toggleOne(uid) {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    emit(next);
  }

  function toggleAll() {
    if (selected.size === rows.length) return emit(new Set());
    emit(new Set(allIds));
  }

  return (
    <div className="bg-white rounded-[10px] shadow-[0_10px_25px_rgba(0,0,0,0.06)] overflow-hidden">
      <table className="w-full text-left">
        <thead className="bg-white">
          <tr className="text-[13px] text-[#111827]">
            {/* чекбокс "все" */}
            <th className="w-[46px] px-4 py-4 border-b border-[#E5E7EB]">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = indeterminate;
                }}
                onChange={toggleAll}
                className="w-4 h-4 accent-[#2B7CFF]"
              />
            </th>

            <th className="w-[80px] px-4 py-4 border-b border-[#E5E7EB]">ID</th>
            <th className="px-4 py-4 border-b border-[#E5E7EB]">Прокси</th>
            <th className="w-[170px] px-4 py-4 border-b border-[#E5E7EB]">Статус</th>
            <th className="w-[140px] px-4 py-4 border-b border-[#E5E7EB] text-center">
              Аккаунтов
            </th>
            <th className="w-[300px] px-4 py-4 border-b border-[#E5E7EB]">Действия</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => {
            const isChecked = selected.has(r.uid);

            return (
              <tr key={r.uid} className="hover:bg-[#F9FAFB] transition">
                <td className="w-[46px] px-4 py-4 border-b border-[#E5E7EB]">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOne(r.uid)}
                    className="w-4 h-4 accent-[#2B7CFF]"
                  />
                </td>

                <td className="px-4 py-4 border-b border-[#E5E7EB] text-[14px] text-[#111827]">
                  {r.uid}
                </td>

                <td className="px-4 py-4 border-b border-[#E5E7EB] text-[14px] text-[#111827]">
                  <span className="inline-block max-w-[520px] truncate align-middle">
                    {r.proxyUrl}
                  </span>
                </td>

                <td className="px-4 py-4 border-b border-[#E5E7EB]">
                  <StatusPill type={r.status} />
                </td>

                <td className="px-4 py-4 border-b border-[#E5E7EB] text-center text-[14px] text-[#111827]">
                  {r.accountsValue ?? 0}
                </td>

                <td className="px-4 py-4 border-b border-[#E5E7EB]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ActionBtn variant="gray" onClick={() => onRecheckOne?.(r.uid)}>
                      Перепроверить
                    </ActionBtn>
                    <ActionBtn variant="yellow" onClick={() => onEditOne?.(r.uid)}>
                      Изменить
                    </ActionBtn>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* выбранные */}
      <div className="px-4 py-3 text-[12px] text-[#6B7280] flex items-center justify-between">
        <div>
          Выбрано:{" "}
          <span className="font-semibold text-[#111827]">{selected.size}</span>
        </div>
      </div>
    </div>
  );
}
